import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, Filter } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Checkbox,
  IconButton,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useGlobalConfig,
  useUpdateGlobalConfig,
  withConfig,
  type ResponseFilteringRule,
} from '@/api/waap';
import { Field } from '@vnetwork/ui/forms';
import { errMessage } from '../waap-util';
import { SaveBar } from './settings-ui';
import styles from './ResponseFilteringSettings.module.css';

const emptyDraft: ResponseFilteringRule = {
  subfilter_replace_path: '',
  subfilter_string_in: '',
  subfilter_is_regex: '0',
  subfilter_string_out: '',
  subfilter_comment: '',
};

/**
 * Response Filtering — search & replace content (a string, or a regex when
 * enabled) in responses before returning to the client. Backed by the
 * global-config `response_filtering` list (whole-object round-trip on save).
 */
export function ResponseFilteringSettings({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: record, isLoading } = useGlobalConfig(domainId);

  const [rules, setRules] = useState<ResponseFilteringRule[]>([]);
  const [draft, setDraft] = useState<ResponseFilteringRule>(emptyDraft);
  // Index of the row being edited (null = the dialog is adding a new rule).
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  // The add/edit rule form lives in a dialog (not an always-visible inline row)
  // so it reads as "add a rule", not part of the page you must fill before Save.
  const [dialogOpen, setDialogOpen] = useState(false);
  // Seed the editable list from the server, but only when the server record
  // actually changes (tracked by `updated`). A bare reference change from a
  // background refetch must NOT clobber rules the user added/edited but hasn't
  // saved yet — otherwise the next save PUTs an empty response_filtering.
  const syncedUpdated = useRef<number | null>(null);
  useEffect(() => {
    if (record && record.updated !== syncedUpdated.current) {
      syncedUpdated.current = record.updated;
      setRules(record.config.response_filtering ?? []);
    }
  }, [record]);

  const update = useUpdateGlobalConfig(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Response Filtering saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const setDraftField = (patch: Partial<ResponseFilteringRule>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const openAdd = () => {
    setDraft(emptyDraft);
    setEditingIdx(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDraft(emptyDraft);
    setEditingIdx(null);
  };

  const commitDraft = () => {
    if (
      !draft.subfilter_replace_path.trim() ||
      !draft.subfilter_string_in.trim() ||
      !draft.subfilter_string_out.trim()
    ) {
      show({ tone: 'danger', title: 'Path, Search Content and Replacement are required' });
      return;
    }
    setRules((r) =>
      editingIdx === null ? [...r, draft] : r.map((rule, i) => (i === editingIdx ? draft : rule)),
    );
    closeDialog();
  };

  const editRule = (idx: number) => {
    setDraft(rules[idx]);
    setEditingIdx(idx);
    setDialogOpen(true);
  };

  const removeRule = (idx: number) => {
    setRules((r) => r.filter((_, i) => i !== idx));
    if (editingIdx === idx) {
      setDraft(emptyDraft);
      setEditingIdx(null);
    }
  };

  const save = () => {
    if (!record) return;
    // Nothing to persist when there are no rules and the server has none either —
    // give feedback instead of a misleading "saved" on an empty form.
    if (rules.length === 0 && (record.config.response_filtering ?? []).length === 0) {
      show({ tone: 'warning', title: 'Add a response filtering rule before saving.' });
      return;
    }
    update.mutate(withConfig(record, { response_filtering: rules }));
  };

  const columns = useMemo<ColumnDef<ResponseFilteringRule & { _idx: number }>[]>(
    () => [
      { accessorKey: 'subfilter_replace_path', header: 'Path' },
      { accessorKey: 'subfilter_string_in', header: 'Search Content' },
      {
        id: 'regex',
        header: 'Regex',
        cell: ({ row }) => (row.original.subfilter_is_regex === '1' ? 'Yes' : 'No'),
      },
      { accessorKey: 'subfilter_string_out', header: 'Replacement' },
      {
        accessorKey: 'subfilter_comment',
        header: 'Description',
        cell: ({ row }) =>
          row.original.subfilter_comment || <span className={styles.muted}>—</span>,
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.rowActions}>
            <IconButton
              aria-label="Edit rule"
              variant="ghost"
              onClick={() => editRule(row.original._idx)}
            >
              <Pencil size={16} />
            </IconButton>
            <IconButton
              aria-label="Delete rule"
              variant="ghost"
              onClick={() => removeRule(row.original._idx)}
            >
              <Trash2 size={16} />
            </IconButton>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules],
  );

  const data = useMemo(() => rules.map((r, i) => ({ ...r, _idx: i })), [rules]);

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <Section
      title="Response Filtering"
      description="Response filtering searches and replaces searched content (a string or a regex if enabled) in responses with a new string before returning to Remote Users."
      actions={
        <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={openAdd}>
          Add Response Filtering Rule
        </Button>
      }
    >
      <DataTable
        columns={columns}
        data={data}
        emptyState={
          <EmptyState
            size="sm"
            icon={<Filter size={20} />}
            title="No filtering rules yet"
            description="Click “Add Response Filtering Rule” to create one."
          />
        }
      />

      <SaveBar onSave={save} saving={update.isPending} disabled={!record} />

      <Dialog open={dialogOpen} onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{editingIdx === null ? 'Add Response Filtering Rule' : 'Edit Response Filtering Rule'}</DialogTitle>
            <DialogDescription>
              Search and replace content (a string, or a regex when enabled) in responses before
              they reach users. Remember to Save after adding.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className={styles.dialogForm}>
              <Field label="Path" required description="URL path of responses to filter. Use * to match all paths.">
                {(props) => (
                  <Input
                    {...props}
                    value={draft.subfilter_replace_path}
                    onChange={(e) => setDraftField({ subfilter_replace_path: e.target.value })}
                    placeholder="/path or *"
                  />
                )}
              </Field>
              <Field label="Search Content" required description="The string (or regular expression when the toggle below is enabled) to find in the response body.">
                {(props) => (
                  <Input
                    {...props}
                    value={draft.subfilter_string_in}
                    onChange={(e) => setDraftField({ subfilter_string_in: e.target.value })}
                    placeholder="Text (or regex) to find"
                  />
                )}
              </Field>
              <Field label="Replacement" required description="The string to substitute in place of each match.">
                {(props) => (
                  <Input
                    {...props}
                    value={draft.subfilter_string_out}
                    onChange={(e) => setDraftField({ subfilter_string_out: e.target.value })}
                    placeholder="Text to replace it with"
                  />
                )}
              </Field>
              <Field label="Description">
                {(props) => (
                  <Input
                    {...props}
                    value={draft.subfilter_comment}
                    onChange={(e) => setDraftField({ subfilter_comment: e.target.value })}
                    placeholder="Optional note"
                  />
                )}
              </Field>
              <label className={styles.regexToggle}>
                <Checkbox
                  checked={draft.subfilter_is_regex === '1'}
                  onCheckedChange={(v) => setDraftField({ subfilter_is_regex: v ? '1' : '0' })}
                />
                <span>Treat Search Content as a regular expression</span>
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={commitDraft}>{editingIdx === null ? 'Add Rule' : 'Update Rule'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
